import React, { useMemo } from "react";
import { Plus, Trash2, Info } from "lucide-react";

/**
 * Ignore / Non-contributing rule with NESTED AND/OR conditions.
 *
 * Internal state is an expression tree:
 *   {type: "group", op: "AND"|"OR", children: [node, ...]}
 *   {type: "condition", col, op, value}
 *
 * Backwards-compat: if incoming cfg has the legacy `conditions: []` array and
 * no `expression`, we wrap it in an AND group. The backend also does this, so
 * old rule sets continue to work.
 */

const OPS = [
  { value: "contains",     label: "contains" },
  { value: "not_contains", label: "does NOT contain" },
  { value: "equals",       label: "equals" },
  { value: "not_equals",   label: "not equal to" },
  { value: "starts_with",  label: "starts with" },
  { value: "ends_with",    label: "ends with" },
  { value: "regex",        label: "matches regex" },
  { value: "is_empty",     label: "is empty" },
  { value: "is_not_empty", label: "is NOT empty" },
  { value: "gt",           label: ">" },
  { value: "gte",          label: "≥" },
  { value: "lt",           label: "<" },
  { value: "lte",          label: "≤" },
  { value: "between",      label: "between (lo,hi)" },
];

const REASONS = [
  { value: "non_contributing", label: "Non-contributing" },
  { value: "outside_period",   label: "Outside this period" },
  { value: "reversed_later",   label: "Reversed in a later period" },
  { value: "already_resolved", label: "Already resolved elsewhere" },
  { value: "bank_fee",         label: "Bank fee / charge" },
  { value: "rounding_dust",    label: "Rounding dust" },
  { value: "other",            label: "Other (see remarks)" },
];

const noValueOps = new Set(["is_empty", "is_not_empty"]);

const BLANK_COND = () => ({ type: "condition", col: "", op: "contains", value: "" });
const BLANK_GROUP = (op = "AND") => ({ type: "group", op, children: [BLANK_COND()] });

function migrate(cfg) {
  // Promote legacy `conditions` → expression, or ensure an expression exists.
  if (cfg.expression) return cfg;
  const conds = cfg.conditions || [];
  return {
    ...cfg,
    expression: {
      type: "group", op: "AND",
      children: conds.length
        ? conds.map(c => ({ type: "condition", ...c }))
        : [BLANK_COND()],
    },
  };
}

export default function IgnoreBlock({ rule, index, onChange }) {
  const cfg = useMemo(() => migrate(rule.ignore_config || {
    side: "both", reason: "non_contributing", remarks: "",
    expression: BLANK_GROUP("AND"),
  }), [rule.ignore_config]);

  const setCfg = (patch) => onChange({ ...rule, ignore_config: { ...cfg, ...patch, conditions: undefined } });
  const setExpression = (expression) => setCfg({ expression });

  return (
    <div data-testid={`ignore-block-${index}`}
      style={{ marginTop: "0.75rem", padding: "0.875rem", background: "#F3F4F6", borderRadius: "0.5rem", border: "1px dashed #6B7280" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: "0.5rem" }}>
        <h4 style={{ margin: 0, fontSize: "0.82rem", fontWeight: 600, color: "#374151" }}>Ignore / Non-contributing rule</h4>
        <p style={{ margin: 0, fontSize: "0.7rem", color: "#6B7280" }}>Runs LAST on leftover unmatched rows.</p>
      </div>

      <div style={{ display: "flex", alignItems: "flex-start", gap: "0.4rem", padding: "0.5rem 0.65rem", background: "rgba(255,255,255,0.6)", border: "1px solid #D1D5DB", borderRadius: "0.375rem", marginBottom: "0.7rem" }}>
        <Info size={13} style={{ color: "#4B5563", flexShrink: 0, marginTop: "2px" }} />
        <p style={{ margin: 0, fontSize: "0.7rem", color: "#4B5563", lineHeight: 1.45 }}>
          Rows where the expression below evaluates to <strong>true</strong> are moved to Ignored. Mix AND / OR groups as needed — click <em>Add group</em> inside a group to nest deeper.
        </p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.6rem", marginBottom: "0.7rem" }}>
        <div>
          <label style={{ fontSize: "0.7rem", color: "#374151", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: "0.2rem" }}>Side to apply</label>
          <select data-testid={`ignore-side-${index}`} value={cfg.side || "both"} onChange={e => setCfg({ side: e.target.value })} className="recon-input" style={{ background: "white", width: "100%" }}>
            <option value="a">File A only</option>
            <option value="b">File B only</option>
            <option value="both">Both sides</option>
          </select>
        </div>
        <div>
          <label style={{ fontSize: "0.7rem", color: "#374151", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: "0.2rem" }}>Reason</label>
          <select data-testid={`ignore-reason-${index}`} value={cfg.reason || "non_contributing"} onChange={e => setCfg({ reason: e.target.value })} className="recon-input" style={{ background: "white", width: "100%" }}>
            {REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>
      </div>

      <div style={{ marginBottom: "0.7rem" }}>
        <label style={{ fontSize: "0.7rem", color: "#374151", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: "0.2rem" }}>Remarks (optional)</label>
        <input data-testid={`ignore-remarks-${index}`} value={cfg.remarks || ""} onChange={e => setCfg({ remarks: e.target.value })} placeholder="e.g. 'Q4 reversals — see ticket #1234'" className="recon-input" style={{ background: "white", width: "100%" }} />
      </div>

      <label style={{ fontSize: "0.7rem", color: "#374151", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: "0.3rem" }}>
        Expression
      </label>
      <GroupEditor node={cfg.expression} path="root" onChange={setExpression} depth={0} ruleIndex={index} isRoot />
    </div>
  );
}


/** Recursive group editor. Renders one group (AND/OR) and its children. */
function GroupEditor({ node, onChange, depth, ruleIndex, isRoot }) {
  const op = (node.op || "AND").toUpperCase();
  const children = node.children || [];

  const setOp = (newOp) => onChange({ ...node, op: newOp });
  const setChild = (i, newChild) => {
    if (newChild === null) {
      onChange({ ...node, children: children.filter((_, j) => j !== i) });
    } else {
      onChange({ ...node, children: children.map((c, j) => j === i ? newChild : c) });
    }
  };
  const addCondition = () => onChange({ ...node, children: [...children, BLANK_COND()] });
  const addGroup = () => onChange({ ...node, children: [...children, BLANK_GROUP(op === "AND" ? "OR" : "AND")] });

  // Color cue per depth to make nesting legible
  const accent = depth % 2 === 0 ? "#4B5563" : "#7C3AED"; // slate / violet
  const bg = depth % 2 === 0 ? "rgba(255,255,255,0.55)" : "rgba(245,243,255,0.85)";

  return (
    <div data-testid={`group-${ruleIndex}-${depth}`}
      style={{
        border: `1px solid ${accent}33`,
        borderLeft: `3px solid ${accent}`,
        borderRadius: 8, padding: "0.55rem 0.65rem",
        marginBottom: "0.35rem",
        background: bg,
      }}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.35rem", marginBottom: "0.45rem" }}>
        <span style={{ fontSize: "0.62rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: accent }}>
          {depth === 0 ? "Match when" : "Group"}
        </span>
        <div style={{ display: "flex", background: "white", border: `1px solid ${accent}55`, borderRadius: 999, overflow: "hidden" }}>
          {["AND", "OR"].map(o => (
            <button key={o}
              data-testid={`group-op-${ruleIndex}-${depth}-${o}`}
              onClick={() => setOp(o)}
              style={{
                padding: "0.15rem 0.6rem", fontSize: "0.68rem", fontWeight: 700,
                background: op === o ? accent : "transparent",
                color: op === o ? "white" : accent,
                border: "none", cursor: "pointer",
              }}>
              {o}
            </button>
          ))}
        </div>
        <span style={{ fontSize: "0.68rem", color: "#6B7280" }}>
          {op === "AND" ? "all of these must be true" : "any of these must be true"}
        </span>
        <div style={{ flex: 1 }} />
        {!isRoot && (
          <button
            data-testid={`group-del-${ruleIndex}-${depth}`}
            onClick={() => onChange(null)}
            title="Remove this group"
            style={{ background: "none", border: "none", cursor: "pointer", color: "#991B1B", padding: "2px" }}>
            <Trash2 size={13} />
          </button>
        )}
      </div>

      <div>
        {children.length === 0 && (
          <p style={{ margin: "0.2rem 0 0.5rem", fontSize: "0.72rem", color: "#9CA3AF", fontStyle: "italic" }}>
            Empty group — this always evaluates false. Add a condition or nested group.
          </p>
        )}
        {children.map((c, i) => (
          <div key={i}>
            {c?.type === "group"
              // Recursive self-call rendered via React.createElement to avoid
              // JSX self-reference (which makes the visual-edits babel plugin
              // infinite-loop while traversing component bindings).
              ? React.createElement(GroupEditor, {
                  node: c,
                  depth: depth + 1,
                  ruleIndex,
                  onChange: nc => setChild(i, nc),
                })
              : (
                <ConditionRow
                  node={c}
                  onChange={nc => setChild(i, nc)}
                  testidPrefix={`cond-${ruleIndex}-${depth}-${i}`}
                />
              )}
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: "0.35rem", marginTop: "0.35rem" }}>
        <button
          data-testid={`group-add-cond-${ruleIndex}-${depth}`}
          onClick={addCondition}
          className="btn-secondary"
          style={{ fontSize: "0.68rem", padding: "0.2rem 0.5rem", background: "white", display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
          <Plus size={10} /> Condition
        </button>
        {depth < 3 && (
          <button
            data-testid={`group-add-group-${ruleIndex}-${depth}`}
            onClick={addGroup}
            style={{ fontSize: "0.68rem", padding: "0.2rem 0.5rem", background: "white", color: accent, border: `1px solid ${accent}`, borderRadius: 6, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
            <Plus size={10} /> Group ({op === "AND" ? "OR" : "AND"})
          </button>
        )}
      </div>
    </div>
  );
}


function ConditionRow({ node, onChange, testidPrefix }) {
  const op = node.op || "contains";
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1.1fr 1fr 1fr 32px", gap: "0.35rem", alignItems: "center", marginBottom: "0.3rem" }}>
      <input
        data-testid={`${testidPrefix}-col`}
        value={node.col || ""}
        onChange={e => onChange({ ...node, col: e.target.value })}
        placeholder="Column name"
        className="recon-input"
        style={{ background: "white" }}
      />
      <select
        data-testid={`${testidPrefix}-op`}
        value={op}
        onChange={e => onChange({ ...node, op: e.target.value })}
        className="recon-input"
        style={{ background: "white" }}>
        {OPS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <input
        data-testid={`${testidPrefix}-value`}
        value={node.value ?? ""}
        onChange={e => onChange({ ...node, value: e.target.value })}
        placeholder={noValueOps.has(op) ? "(n/a)" : op === "between" ? "lo,hi" : op === "regex" ? "regex" : "value"}
        disabled={noValueOps.has(op)}
        className="recon-input"
        style={{ background: noValueOps.has(op) ? "#F9FAFB" : "white" }}
      />
      <button
        data-testid={`${testidPrefix}-del`}
        onClick={() => onChange(null)}
        style={{ background: "none", border: "none", cursor: "pointer", color: "#991B1B", padding: "2px" }}>
        <Trash2 size={13} />
      </button>
    </div>
  );
}
